import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { downloadCSV, inr, EmptyRow } from "./utils";

export default function StockAlertsReport() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("low");

  const load = () => {
    setBusy(true);
    api.get("/reports/inventory/alerts")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, []);

  const counts = data?.counts || { low: 0, high: 0, ok: 0 };
  const rows = data ? (view === "low" ? data.low_stock : view === "high" ? data.high_stock : data.ok_stock) : [];

  const exportCSV = () => downloadCSV(
    `stock-alerts-${view}`,
    ["Status", "SKU", "Name", "Category", "Unit", "Current Stock", "Min Level", "Max Level", "Reorder", "Shortfall/Excess", "Unit Cost", "Supplier"],
    rows.map((r) => [
      r.status, r.sku, r.name, r.category, r.unit, r.current_stock,
      r.min_stock_level, r.max_stock_level, r.reorder_level,
      r.shortfall ?? r.excess ?? "", r.unit_cost, r.supplier,
    ])
  );

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <div onClick={() => setView("low")}
          data-testid="alerts-tile-low"
          className={`cursor-pointer border ${view === "low" ? "border-rose-500 bg-rose-50" : "border-neutral-200 bg-white"} px-4 py-3`}>
          <div className="label-caps mb-1 text-[10px] text-rose-700">Low stock (below min / reorder)</div>
          <div className="font-mono-num text-2xl font-bold text-rose-700">{counts.low}</div>
        </div>
        <div onClick={() => setView("high")}
          data-testid="alerts-tile-high"
          className={`cursor-pointer border ${view === "high" ? "border-amber-500 bg-amber-50" : "border-neutral-200 bg-white"} px-4 py-3`}>
          <div className="label-caps mb-1 text-[10px] text-amber-700">High stock (above max)</div>
          <div className="font-mono-num text-2xl font-bold text-amber-700">{counts.high}</div>
        </div>
        <div onClick={() => setView("ok")}
          data-testid="alerts-tile-ok"
          className={`cursor-pointer border ${view === "ok" ? "border-emerald-500 bg-emerald-50" : "border-neutral-200 bg-white"} px-4 py-3`}>
          <div className="label-caps mb-1 text-[10px] text-emerald-700">Healthy stock</div>
          <div className="font-mono-num text-2xl font-bold text-emerald-700">{counts.ok}</div>
        </div>
      </div>

      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <Button data-testid="alerts-refresh" onClick={load} disabled={busy} variant="outline" className="rounded-sm">
          {busy ? "Loading…" : "Refresh"}
        </Button>
        <Button data-testid="alerts-export" onClick={exportCSV} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          Download .csv
        </Button>
        <div className="ml-auto text-xs text-neutral-500">
          Set <span className="font-medium">min_stock_level</span> and <span className="font-medium">max_stock_level</span> on materials to unlock these alerts.
        </div>
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="alerts-table">
          <thead className="bg-neutral-50 label-caps">
            <tr>
              <th className="text-left px-4 py-2.5">SKU</th>
              <th className="text-left px-4 py-2.5">Name</th>
              <th className="text-right px-4 py-2.5">Current Stock</th>
              <th className="text-right px-4 py-2.5">Min</th>
              <th className="text-right px-4 py-2.5">Max</th>
              <th className="text-right px-4 py-2.5">{view === "low" ? "Shortfall" : view === "high" ? "Excess" : "Buffer"}</th>
              <th className="text-left px-4 py-2.5">Supplier</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow cols={7} msg={`No ${view === "low" ? "low" : view === "high" ? "high" : "healthy"}-stock items.`} />}
            {rows.map((r) => (
              <tr key={r.sku} className="border-t border-neutral-100 data-row" data-testid={`alerts-row-${r.sku}`}>
                <td className="px-4 py-2.5 font-mono-num text-xs">{r.sku}</td>
                <td className="px-4 py-2.5">{r.name}<div className="text-[10px] text-neutral-400 uppercase">{r.category}</div></td>
                <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{inr(r.current_stock)} <span className="text-[10px] text-neutral-500">{r.unit}</span></td>
                <td className="px-4 py-2.5 text-right font-mono-num text-neutral-600">{r.min_stock_level ?? "—"}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-neutral-600">{r.max_stock_level ?? "—"}</td>
                <td className={`px-4 py-2.5 text-right font-mono-num font-semibold ${view === "low" ? "text-rose-700" : view === "high" ? "text-amber-700" : "text-emerald-700"}`}>
                  {view === "low" ? (r.shortfall != null ? inr(r.shortfall) : "—")
                    : view === "high" ? (r.excess != null ? inr(r.excess) : "—")
                    : "—"}
                </td>
                <td className="px-4 py-2.5 text-xs text-neutral-600">{r.supplier || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// 5. Jobwise RM % Report
// ============================================================================
